import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface Props {
  value: string;
  onChange: (v: string) => void;
  className?: string;
  placeholder?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

/**
 * Minutes come in fives. Sixty rows of 36px in a 220px window meant scrolling
 * half an hour of list to reach 09:30, which is most of what "works like shit"
 * meant. Anything off the grid is still reachable — you type it.
 */
const MINUTE_STEP = 5;
const MINUTES = Array.from({ length: 60 / MINUTE_STEP }, (_, i) => String(i * MINUTE_STEP).padStart(2, '0'));

/**
 * ...and a value that is already off the grid keeps its own row, so a flight at
 * 09:37 does not vanish from its own picker the moment you open it.
 */
function minuteOptions(current?: string): string[] {
  if (!current || MINUTES.includes(current)) return MINUTES;
  return [...MINUTES, current].sort((a, b) => Number(a) - Number(b));
}

function parseTime(v: string): { h: string; m: string } | null {
  if (!v) return null;
  const [h, m] = v.split(':');
  if (h === undefined || m === undefined) return null;
  return { h: h.padStart(2, '0'), m: m.padStart(2, '0') };
}

const valid = (h: number, m: number) => h <= 23 && m <= 59;
const hhmm = (h: number, m: number) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

/**
 * A time the user has finished typing: stored while they type, the moment the
 * field holds one. Only the full HH:MM form counts here — "14:3" is 14:30 on
 * its way, and reading it early as 1:43 is what made typing into an empty field
 * jump to 01:43.
 */
function completeTime(raw: string): string | null {
  const match = raw.match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  return valid(h, m) ? hhmm(h, m) : null;
}

/**
 * What a user means once they leave the field or press Enter: "9" → 09:00,
 * "14h" → 14:00, "9.30"/"9h30"/"9:3" → 09:30, "930" → 09:30, "1430" → 14:30.
 * Short forms are read only here, never mid-typing (see `completeTime`).
 */
function committedTime(raw: string): string | null {
  const text = raw.trim();
  const split = text.match(/^(\d{1,2})(?:[:.,hH](\d{0,2}))?$/);
  if (split) {
    const h = Number(split[1]);
    const m = Number((split[2] ?? '').padEnd(2, '0'));
    return valid(h, m) ? hhmm(h, m) : null;
  }
  const digits = text.match(/^\d{3,4}$/)?.[0];
  if (!digits) return null;
  const h = Number(digits.slice(0, -2));
  const m = Number(digits.slice(-2));
  return valid(h, m) ? hhmm(h, m) : null;
}

export default function TimePicker({ value, onChange, className = '', placeholder }: Props) {
  const { t } = useTranslation();
  const parsed = parseTime(value);

  const [open, setOpen]       = useState(false);
  const [pos, setPos]         = useState({ top: 0, left: 0 });
  const [inputVal, setInputVal] = useState(parsed ? `${parsed.h}:${parsed.m}` : '');

  const wrapRef    = useRef<HTMLDivElement>(null);
  const inputRef   = useRef<HTMLInputElement>(null);
  const clockRef   = useRef<HTMLButtonElement>(null);
  const popupRef   = useRef<HTMLDivElement>(null);
  const justFocused = useRef(false);
  const hourRef    = useRef<HTMLDivElement>(null);
  const minuteRef  = useRef<HTMLDivElement>(null);

  // Sync inputVal when value changes externally — but not over what the user
  // is typing when it already says the same time. Adjusted during render, not
  // in an effect, so the field never paints a frame with the stale text.
  const [syncedValue, setSyncedValue] = useState(value);
  if (value !== syncedValue) {
    setSyncedValue(value);
    const shown = parsed ? `${parsed.h}:${parsed.m}` : '';
    setInputVal(prev => (committedTime(prev) ?? '') === shown ? prev : shown);
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (
        wrapRef.current?.contains(e.target as Node) ||
        popupRef.current?.contains(e.target as Node)
      ) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Scroll selected items into view when opening
  useEffect(() => {
    if (!open) return;
    setTimeout(() => {
      hourRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'center' });
      minuteRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'center' });
    }, 10);
  }, [open]);

  const calcPos = useCallback(() => {
    const anchor = wrapRef.current;
    if (!anchor) return;
    const r          = anchor.getBoundingClientRect();
    const popH       = 260;
    const popW       = 180;
    const spaceBelow = window.innerHeight - r.bottom - 8;
    const spaceAbove = r.top - 8;
    const openUp     = spaceBelow < popH && spaceAbove > spaceBelow;
    const top  = openUp ? r.top - popH - 6 : r.bottom + 6;
    const left = Math.min(r.left, window.innerWidth - popW - 8);
    // Same numbers, same object: scrolling fires this continuously and a fresh
    // object every time would re-render the whole list on each event.
    setPos(prev => (prev.top === top && prev.left === left ? prev : { top, left }));
  }, []);

  // The popup is a fixed-position portal, so its coordinates are frozen at the
  // moment it opens. The forms live inside `TripDetailPage`'s scrolling
  // container, which meant the menu stayed floating where it was while its own
  // field slid out from under it. `capture` is what catches that ancestor's
  // scroll as well as the window's.
  useEffect(() => {
    if (!open) return;
    const reposition = () => calcPos();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, calcPos]);

  function togglePicker() {
    calcPos();
    setOpen(o => !o);
  }

  function select(h: string, m: string) {
    onChange(`${h}:${m}`);
    setInputVal(`${h}:${m}`);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    // A separator typed by hand right after the automatic colon ("14:" + ":"
    // or "h") is that same colon, not a second one.
    let raw = e.target.value.replace(/:[:.,hH]/, ':');
    // Auto-insert the colon after two digits — but ONLY while typing forwards.
    // Doing it unconditionally meant backspace deleted the colon and this line
    // put it straight back, with the caret jumping to the end: the field could
    // not be emptied except by selecting all of it. That single line is most of
    // what "the time thing works like shit" was about.
    const deleting = raw.length < inputVal.length;
    // Only after a real hour: "93" is the start of 9:30, not an hour 93.
    if (!deleting && /^\d{2}$/.test(raw) && Number(raw) <= 23) raw = raw + ':';
    setInputVal(raw);
    const complete = completeTime(raw);
    if (complete) onChange(complete);
    else if (!raw) onChange('');
  }

  function commit() {
    const time = committedTime(inputVal);
    if (time) {
      setInputVal(time);
      if (time !== value) onChange(time);
    } else if (inputVal) {
      // Revert to last valid value
      const p = parseTime(value);
      setInputVal(p ? `${p.h}:${p.m}` : '');
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      // Enter settles the time rather than submitting the form around it with
      // a half-typed value still in the field.
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape' && open) {
      setOpen(false);
    }
  }

  const minutes = useMemo(() => minuteOptions(parsed?.m), [parsed?.m]);

  const popup = open && (
    <div
      ref={popupRef}
      className="fixed z-[9999] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl overflow-hidden animate-fade-in"
      style={{ top: pos.top, left: pos.left, width: 180 }}
    >
      <div className="flex">
        {/* Hours column */}
        <div ref={hourRef} className="flex-1 overflow-y-auto border-r border-slate-100 dark:border-slate-800" style={{ maxHeight: 220 }}>
          <div className="py-1">
            {HOURS.map(h => {
              const sel = parsed?.h === h;
              return (
                <button key={h} type="button" data-selected={sel}
                  onClick={() => select(h, parsed?.m ?? '00')}
                  className={`w-full h-9 font-mono text-[13px] font-semibold transition-colors cursor-pointer
                    ${sel ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                >
                  {h}
                </button>
              );
            })}
          </div>
        </div>

        {/* Minutes column */}
        <div ref={minuteRef} className="flex-1 overflow-y-auto" style={{ maxHeight: 220 }}>
          <div className="py-1">
            {minutes.map(m => {
              const sel = parsed?.m === m;
              return (
                <button key={m} type="button" data-selected={sel}
                  onClick={() => select(parsed?.h ?? '00', m)}
                  className={`w-full h-9 font-mono text-[13px] font-semibold transition-colors cursor-pointer
                    ${sel ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                >
                  {m}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 dark:border-slate-800">
        <span className="font-mono text-[11px] font-semibold text-slate-500 dark:text-slate-400 tracking-widest">
          {parsed ? `${parsed.h}:${parsed.m}` : '--:--'}
        </span>
        {value && (
          <button type="button" onClick={() => { onChange(''); setInputVal(''); setOpen(false); }}
            className="font-mono text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors cursor-pointer">
            {t('common.clear')}
          </button>
        )}
        <button type="button" onClick={() => setOpen(false)}
          className="h-7 px-3 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-mono text-[10px] font-semibold cursor-pointer hover:bg-slate-700 dark:hover:bg-slate-100 transition-colors">
          {t('common.done')}
        </button>
      </div>
    </div>
  );

  return (
    <div ref={wrapRef} className={`relative flex input p-0 overflow-hidden ${className}`}>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={inputVal}
        onChange={handleInputChange}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        // Typing over a time replaces it: a full field used to swallow every key.
        // Selected again on the click that focused it, which otherwise drops the
        // caret where the pointer was and undoes the selection.
        onFocus={e => { e.target.select(); justFocused.current = true; }}
        onClick={e => { if (justFocused.current) e.currentTarget.select(); justFocused.current = false; }}
        placeholder={placeholder ?? '--:--'}
        maxLength={5}
        className="flex-1 min-w-0 bg-transparent px-3 py-2 font-mono text-[13px] font-semibold text-slate-800 dark:text-slate-200 placeholder:text-slate-400 outline-none"
      />
      <button
        ref={clockRef}
        type="button"
        onClick={togglePicker}
        aria-label={t('common.pickTime')}
        aria-expanded={open}
        className="flex items-center justify-center w-10 shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors cursor-pointer border-l border-slate-200 dark:border-slate-700"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
      </button>
      {typeof document !== 'undefined' && createPortal(popup, document.body)}
    </div>
  );
}
